import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  input,
  output,
  signal,
  effect,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { DialogModule } from 'primeng/dialog';
import { DrawerModule } from 'primeng/drawer';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ConfirmationService } from 'primeng/api';
import { AgenticAiApiService } from '../../agentic-ai-api.service';
import {
  assignableUserLabel,
  TOKEN_ALLOCATION_MIN_SEARCH_LENGTH,
  defaultUserQuotaRenewalOn,
  parseUserQuotaRenewalType,
  type AssignableUserOption,
  type CompanyTokenAllocationSummary,
  type CreateUserTokenQuotaDto,
  computeTokenUsagePercentages,
  consumedPercentBarTone,
  allocatedBelowUsedTokensMessage,
  isAllocatedBelowUsedTokens,
  type TokenUsageBarTone,
  type TokenUsagePercentages,
  type TokenAllocationActivity,
  type UpdateUserTokenQuotaDto,
  USER_QUOTA_AGENT_CHART_COLORS,
  USER_QUOTA_RENEWAL_TYPE_OPTIONS,
  USER_QUOTA_USAGE_PERIOD_OPTIONS,
  userQuotaRenewalOnLabel,
  userQuotaRenewalOnOptions,
  userQuotaRenewalTypeLabel,
  type UserQuotaRenewalType,
  type UserQuotaUsageMetrics,
  type UserQuotaUsagePeriod,
  type UserQuotaListStatus,
  type UserTokenQuota,
} from '../../token-allocation.models';
import { ToastService } from '../../../../shared/services/toast.service';
import { UserService } from '../../../../shared/services/user.service';
import { formatBqDateScalarForDisplay } from '../../../../shared/utils/company-date-format.util';

type QuotaFormMode = 'create' | 'edit';

interface QuotaFormState {
  mode: QuotaFormMode;
  quotaId?: string;
  userLabel?: string;
  selectedUserId: string | null;
  allocatedTokens: number;
  usedTokens: number;
  renewalType: UserQuotaRenewalType;
  renewalOn: number | null;
  renewalStatus: 0 | 1;
  renewalTokens: number;
}

interface AddTokensFormState {
  quotaId: string;
  userLabel: string;
  allocatedTokens: number;
  renewalType: UserQuotaRenewalType;
  renewalOn: number | null;
  addTokens: number;
}

interface AssignableUserSelectOption extends AssignableUserOption {
  label: string;
}

interface QuotaListViewOption {
  label: string;
  value: UserQuotaListStatus;
}

interface UsageDonutSegment {
  agentCode: string;
  agentName: string;
  tokens: number;
  color: string;
  dashArray: string;
  dashOffset: number;
}

const USAGE_DONUT_RADIUS = 40;
const USAGE_DONUT_CIRCUMFERENCE = 2 * Math.PI * USAGE_DONUT_RADIUS;

@Component({
  selector: 'app-tokens-allocation-drawer',
  imports: [
    FormsModule,
    DrawerModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    DialogModule,
    ProgressSpinnerModule,
    ConfirmDialogModule,
    TooltipModule,
    ToggleSwitchModule,
  ],
  templateUrl: './tokens-allocation-drawer.html',
  styleUrl: './tokens-allocation-drawer.scss',
  providers: [ConfirmationService],
})
export class TokensAllocationDrawer {
  readonly visible = input(false);
  readonly visibleChange = output<boolean>();

  readonly companySummary = signal<CompanyTokenAllocationSummary | null>(null);
  readonly quotas = signal<UserTokenQuota[]>([]);
  readonly quotaListView = signal<UserQuotaListStatus>('active');
  readonly quotaListViewOptions: QuotaListViewOption[] = [
    { label: 'Active allocations', value: 'active' },
    { label: 'Deleted allocations', value: 'deleted' },
  ];
  readonly restoringQuotaId = signal<string | null>(null);
  readonly totalRecords = signal(0);
  readonly loading = signal(false);
  readonly drawerLoading = signal(false);
  readonly drawerDataLoaded = signal(false);
  readonly accessDenied = signal(false);
  readonly listError = signal<string | null>(null);

  readonly showActivities = signal(false);
  readonly showUserMetrics = signal(false);
  readonly metricsQuota = signal<UserTokenQuota | null>(null);
  readonly usageMetrics = signal<UserQuotaUsageMetrics | null>(null);
  readonly usageMetricsLoading = signal(false);
  readonly usageMetricsError = signal<string | null>(null);
  readonly usageMetricsPeriod = signal<UserQuotaUsagePeriod>('this_month');
  readonly usagePeriodOptions = USER_QUOTA_USAGE_PERIOD_OPTIONS;
  readonly renewalTypeOptions = USER_QUOTA_RENEWAL_TYPE_OPTIONS;
  readonly activities = signal<TokenAllocationActivity[]>([]);
  readonly activitiesTotal = signal(0);
  readonly activitiesLoading = signal(false);
  readonly activitiesError = signal<string | null>(null);
  readonly activitiesPage = signal(1);
  readonly activitiesPageSize = signal(20);
  readonly activitiesTableFirst = signal(0);

  /** Applied filter sent to the quotas API. */
  readonly searchText = signal('');
  /** Bound to the quotas search input. */
  readonly searchInput = signal('');

  readonly activitySearchText = signal('');
  readonly activitySearchInput = signal('');

  readonly page = signal(1);
  readonly pageSize = signal(20);
  readonly tableFirst = signal(0);

  readonly formVisible = signal(false);
  readonly formSaving = signal(false);
  readonly addTokensVisible = signal(false);
  readonly addTokensSaving = signal(false);
  readonly assignableUsersLoading = signal(false);
  readonly assignableUserOptions = signal<AssignableUserSelectOption[]>([]);

  readonly deleteQuotaConfirmKey = 'tokenAllocationDelete';
  deleteConfirmUserLabel = '';

  private formState: QuotaFormState = this.emptyForm('create');
  private addTokensState: AddTokensFormState | null = null;
  private pendingDeleteRow: UserTokenQuota | null = null;
  private skipNextLazyLoad = false;
  private skipNextActivityLazyLoad = false;

  private readonly api = inject(AgenticAiApiService);
  private readonly toast = inject(ToastService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly userService = inject(UserService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => {
      if (this.visible()) {
        this.loadDrawerData();
      }
    });
  }

  onDrawerVisibleChange(open: boolean): void {
    this.visibleChange.emit(open);
    if (!open) {
      this.closeForm();
      this.closeAddTokens();
      this.resetDrawerState();
    }
  }

  formatTokens(n: number): string {
    return Number(n || 0).toLocaleString();
  }

  companyUsage(summary: CompanyTokenAllocationSummary): TokenUsagePercentages {
    return computeTokenUsagePercentages(summary.allocatedTokens, summary.usedTokens);
  }

  userUsage(row: UserTokenQuota): TokenUsagePercentages {
    return computeTokenUsagePercentages(row.allocatedTokens, row.usedTokens);
  }

  consumedBarTone(consumedPercent: number): TokenUsageBarTone {
    return consumedPercentBarTone(consumedPercent);
  }

  formatActivityDateTime(iso: string): string {
    return this.formatQuotaDateTime(iso);
  }

  formatQuotaDateTime(iso: string): string {
    const trimmed = iso?.trim();
    if (!trimmed) {
      return '—';
    }
    const companyFormat = this.userService.getCompanyDateFormat();
    const formatted = formatBqDateScalarForDisplay(trimmed, companyFormat, 'created_at');
    return formatted ?? trimmed;
  }

  formatRenewalType(value: UserQuotaRenewalType | number): string {
    return userQuotaRenewalTypeLabel(parseUserQuotaRenewalType(value));
  }

  formatRenewalOn(renewalType: UserQuotaRenewalType, renewalOn: number | null): string {
    return userQuotaRenewalOnLabel(renewalType, renewalOn);
  }

  showFormRenewalOn(): boolean {
    const type = this.formState.renewalType;
    return type === 1 || type === 2;
  }

  formRenewalOnOptions() {
    return userQuotaRenewalOnOptions(this.formState.renewalType);
  }

  onFormRenewalTypeChange(type: UserQuotaRenewalType): void {
    this.formState.renewalType = type;
    this.formState.renewalOn = defaultUserQuotaRenewalOn(type);
  }

  hasQuotaSearchText(): boolean {
    return this.searchInput().trim().length > 0;
  }

  hasActivitySearchText(): boolean {
    return this.activitySearchInput().trim().length > 0;
  }

  canRunActivitySearch(): boolean {
    return this.activitySearchInput().trim().length >= TOKEN_ALLOCATION_MIN_SEARCH_LENGTH;
  }

  clearQuotaSearch(): void {
    this.searchInput.set('');
    this.searchText.set('');
    this.page.set(1);
    this.tableFirst.set(0);
    this.loadQuotas();
  }

  runQuotaSearch(): void {
    const q = this.searchInput().trim();
    if (q.length > 0 && q.length < TOKEN_ALLOCATION_MIN_SEARCH_LENGTH) {
      this.toast.warn(`Enter at least ${TOKEN_ALLOCATION_MIN_SEARCH_LENGTH} characters to search.`);
      return;
    }
    this.searchText.set(q);
    this.page.set(1);
    this.tableFirst.set(0);
    this.loadQuotas();
  }

  onQuotaListViewChange(view: UserQuotaListStatus): void {
    if (this.quotaListView() === view) {
      return;
    }
    this.quotaListView.set(view);
    this.page.set(1);
    this.tableFirst.set(0);
    this.skipNextLazyLoad = true;
    this.loadQuotas();
  }

  isActiveQuotaView(): boolean {
    return this.quotaListView() === 'active';
  }

  quotaEmptyMessage(): string {
    if (this.quotaListView() === 'deleted') {
      return this.searchText().trim()
        ? 'No deleted allocations match your search.'
        : 'No deleted allocations.';
    }
    return this.searchText().trim()
      ? 'No active allocations match your search.'
      : 'No active allocations.';
  }

  clearActivitySearch(): void {
    this.activitySearchInput.set('');
    this.activitySearchText.set('');
    this.activitiesPage.set(1);
    this.activitiesTableFirst.set(0);
    this.loadActivities();
  }

  runActivitySearch(): void {
    if (!this.canRunActivitySearch()) {
      return;
    }
    const q = this.activitySearchInput().trim();
    this.activitySearchText.set(q);
    this.activitiesPage.set(1);
    this.activitiesTableFirst.set(0);
    this.loadActivities();
  }

  onPageChange(event: TableLazyLoadEvent): void {
    if (this.skipNextLazyLoad) {
      this.skipNextLazyLoad = false;
      return;
    }
    if (!this.drawerDataLoaded() || this.drawerLoading()) {
      return;
    }
    const rows = event.rows ?? this.pageSize();
    const first = event.first ?? 0;
    this.pageSize.set(rows);
    this.tableFirst.set(first);
    this.page.set(Math.floor(first / rows) + 1);
    this.loadQuotas();
  }

  onActivityPageChange(event: TableLazyLoadEvent): void {
    if (this.skipNextActivityLazyLoad) {
      this.skipNextActivityLazyLoad = false;
      return;
    }
    if (!this.showActivities()) {
      return;
    }
    const rows = event.rows ?? this.activitiesPageSize();
    const first = event.first ?? 0;
    this.activitiesPageSize.set(rows);
    this.activitiesTableFirst.set(first);
    this.activitiesPage.set(Math.floor(first / rows) + 1);
    this.loadActivities();
  }

  openActivities(): void {
    this.showUserMetrics.set(false);
    this.metricsQuota.set(null);
    this.showActivities.set(true);
    this.activitiesPage.set(1);
    this.activitiesTableFirst.set(0);
    this.activitySearchInput.set('');
    this.activitySearchText.set('');
    this.skipNextActivityLazyLoad = true;
    this.loadActivities();
  }

  closeActivities(): void {
    this.showActivities.set(false);
    this.activities.set([]);
    this.activitiesTotal.set(0);
    this.activitiesError.set(null);
    this.activitySearchInput.set('');
    this.activitySearchText.set('');
  }

  openUserMetrics(row: UserTokenQuota): void {
    this.showActivities.set(false);
    this.metricsQuota.set(row);
    this.showUserMetrics.set(true);
    this.usageMetricsPeriod.set('this_month');
    this.usageMetrics.set(null);
    this.usageMetricsError.set(null);
    this.loadUsageMetrics();
  }

  closeUserMetrics(): void {
    this.showUserMetrics.set(false);
    this.metricsQuota.set(null);
    this.usageMetrics.set(null);
    this.usageMetricsError.set(null);
    this.usageMetricsLoading.set(false);
    this.usageMetricsPeriod.set('this_month');
  }

  metricsUserLabel(): string {
    const row = this.metricsQuota();
    return row ? this.userRowLabel(row) : 'User';
  }

  onUsagePeriodChange(period: UserQuotaUsagePeriod): void {
    if (this.usageMetricsPeriod() === period) {
      return;
    }
    this.usageMetricsPeriod.set(period);
    this.loadUsageMetrics();
  }

  agentChartColor(agentCode: string): string {
    return USER_QUOTA_AGENT_CHART_COLORS[agentCode] ?? '#94a3b8';
  }

  agentBarWidth(agent: { tokens: number }, totalTokens: number): number {
    const total = Math.max(0, Number(totalTokens) || 0);
    if (total <= 0) {
      return 0;
    }
    return Math.min(100, Math.round((Math.max(0, agent.tokens) / total) * 100));
  }

  usageDonutSegments(metrics: UserQuotaUsageMetrics): UsageDonutSegment[] {
    const total = Math.max(0, Number(metrics.totalTokens) || 0);
    if (total <= 0) {
      return [];
    }
    let offset = 0;
    const segments: UsageDonutSegment[] = [];
    for (const agent of metrics.byAgent) {
      if (agent.tokens <= 0) {
        continue;
      }
      const length = (agent.tokens / total) * USAGE_DONUT_CIRCUMFERENCE;
      segments.push({
        agentCode: agent.agentCode,
        agentName: agent.agentName,
        tokens: agent.tokens,
        color: this.agentChartColor(agent.agentCode),
        dashArray: `${length} ${USAGE_DONUT_CIRCUMFERENCE}`,
        dashOffset: -offset,
      });
      offset += length;
    }
    return segments;
  }

  private loadUsageMetrics(): void {
    const row = this.metricsQuota();
    const quotaId = row?.id?.trim();
    if (!quotaId || this.accessDenied()) {
      return;
    }
    this.usageMetricsLoading.set(true);
    this.usageMetricsError.set(null);
    this.api
      .getUserQuotaUsageMetrics(quotaId, this.usageMetricsPeriod())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.usageMetrics.set(res);
          this.usageMetricsLoading.set(false);
          this.cdr.markForCheck();
        },
        error: (err: unknown) => {
          this.usageMetricsLoading.set(false);
          this.usageMetricsError.set(
            this.extractErrorMessage(err, 'Could not load token usage metrics.'),
          );
          this.cdr.markForCheck();
        },
      });
  }

  openCreate(): void {
    this.formState = this.emptyForm('create');
    this.formVisible.set(true);
    this.loadAssignableUsers();
  }

  openEdit(row: UserTokenQuota): void {
    this.formState = {
      mode: 'edit',
      quotaId: row.id,
      userLabel: this.userRowLabel(row),
      selectedUserId: null,
      allocatedTokens: row.allocatedTokens,
      usedTokens: row.usedTokens,
      renewalType: row.renewalType,
      renewalOn: row.renewalOn,
      renewalStatus: row.renewalStatus,
      renewalTokens: row.renewalTokens,
    };
    this.formVisible.set(true);
  }

  openAddTokens(row: UserTokenQuota): void {
    this.addTokensState = {
      quotaId: row.id,
      userLabel: this.userRowLabel(row),
      allocatedTokens: row.allocatedTokens,
      renewalType: row.renewalType,
      renewalOn: row.renewalOn,
      addTokens: 0,
    };
    this.addTokensVisible.set(true);
  }

  closeAddTokens(): void {
    this.addTokensVisible.set(false);
    this.addTokensState = null;
  }

  onAddTokensVisibleChange(open: boolean): void {
    if (!open) {
      this.closeAddTokens();
      return;
    }
    this.addTokensVisible.set(true);
  }

  get addTokensUserLabel(): string {
    return this.addTokensState?.userLabel ?? '';
  }

  get addTokensAllocatedLabel(): string {
    return this.formatTokens(this.addTokensState?.allocatedTokens ?? 0);
  }

  get addTokensRenewalTypeLabel(): string {
    const state = this.addTokensState;
    return state ? this.formatRenewalType(state.renewalType) : '—';
  }

  get addTokensRenewalOnLabel(): string {
    const state = this.addTokensState;
    return state ? this.formatRenewalOn(state.renewalType, state.renewalOn) : '—';
  }

  get addTokensAmount(): number {
    return this.addTokensState?.addTokens ?? 0;
  }
  set addTokensAmount(v: number) {
    if (this.addTokensState) {
      this.addTokensState.addTokens = v;
    }
  }

  saveAddTokens(): void {
    if (this.addTokensSaving() || !this.addTokensState) {
      return;
    }
    const addTokens = Math.max(0, Math.floor(this.addTokensState.addTokens || 0));
    if (addTokens <= 0) {
      this.toast.warn('Enter a token amount greater than zero.');
      return;
    }
    const quotaId = this.addTokensState.quotaId?.trim();
    if (!quotaId) {
      return;
    }
    this.addTokensSaving.set(true);
    this.api
      .addUserTokenQuotaTokens(quotaId, { addTokens })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.addTokensSaving.set(false);
          this.toast.success('Tokens added to allocation.');
          this.closeAddTokens();
          this.reloadDrawerData();
        },
        error: (err: unknown) => {
          this.addTokensSaving.set(false);
          this.toast.error(this.extractErrorMessage(err, 'Could not add tokens.'));
        },
      });
  }

  get formMode(): QuotaFormMode {
    return this.formState.mode;
  }

  get formUserLabel(): string {
    return this.formState.userLabel ?? '';
  }

  get formSelectedUserId(): string | null {
    return this.formState.selectedUserId;
  }
  set formSelectedUserId(v: string | null) {
    this.formState.selectedUserId = v;
  }

  get formAllocatedTokens(): number {
    return this.formState.allocatedTokens;
  }
  set formAllocatedTokens(v: number) {
    this.formState.allocatedTokens = v;
  }

  get formRenewalType(): UserQuotaRenewalType {
    return this.formState.renewalType;
  }
  set formRenewalType(v: UserQuotaRenewalType) {
    this.onFormRenewalTypeChange(v);
  }

  get formRenewalOn(): number | null {
    return this.formState.renewalOn;
  }
  set formRenewalOn(v: number | null) {
    this.formState.renewalOn = v;
  }

  get formRenewalStatusOn(): boolean {
    return this.formState.renewalStatus === 1;
  }
  set formRenewalStatusOn(v: boolean) {
    this.formState.renewalStatus = v ? 1 : 0;
  }

  get formRenewalTokens(): number {
    return this.formState.renewalTokens;
  }
  set formRenewalTokens(v: number) {
    this.formState.renewalTokens = v;
  }

  get formUsedTokens(): number {
    return this.formState.usedTokens;
  }

  formAllocatedBelowUsed(): boolean {
    if (this.formState.mode !== 'edit') {
      return false;
    }
    return isAllocatedBelowUsedTokens(this.formState.allocatedTokens, this.formState.usedTokens);
  }

  formAllocationError(): string | null {
    if (!this.formAllocatedBelowUsed()) {
      return null;
    }
    return allocatedBelowUsedTokensMessage(this.formState.usedTokens);
  }

  canSubmitForm(): boolean {
    if (this.formSaving()) {
      return false;
    }
    return !this.formAllocatedBelowUsed();
  }

  closeForm(): void {
    this.formVisible.set(false);
    this.formState = this.emptyForm('create');
    this.assignableUserOptions.set([]);
  }

  saveForm(): void {
    if (this.formSaving()) {
      return;
    }
    const allocatedTokens = Math.max(0, Math.floor(this.formState.allocatedTokens || 0));
    const renewalType = this.formState.renewalType;
    const renewalOn = renewalType === 0 ? null : this.formState.renewalOn;
    const renewalStatus = this.formState.renewalStatus;

    if ((renewalType === 1 || renewalType === 2) && renewalOn == null) {
      this.toast.warn('Select a renewal day.');
      return;
    }

    if (this.formState.mode === 'edit' && isAllocatedBelowUsedTokens(allocatedTokens, this.formState.usedTokens)) {
      this.toast.error(allocatedBelowUsedTokensMessage(this.formState.usedTokens));
      return;
    }

    if (this.formState.mode === 'create') {
      const userId = this.formState.selectedUserId?.trim();
      if (!userId) {
        this.toast.warn('Select a user.');
        return;
      }
      const body: CreateUserTokenQuotaDto = {
        userId,
        allocatedTokens,
        renewalType,
        renewalOn,
        renewalStatus,
      };
      this.formSaving.set(true);
      this.api
        .createUserTokenQuota(body)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.formSaving.set(false);
            this.toast.success('Tokens assigned.');
            this.closeForm();
            this.reloadDrawerData();
          },
          error: (err: unknown) => {
            this.formSaving.set(false);
            this.toast.error(this.extractErrorMessage(err, 'Could not assign tokens.'));
          },
        });
      return;
    }

    const quotaId = this.formState.quotaId?.trim();
    if (!quotaId) {
      return;
    }
    const renewalTokens = Math.max(0, Math.floor(this.formState.renewalTokens || 0));
    const body: UpdateUserTokenQuotaDto = {
      allocatedTokens,
      renewalType,
      renewalOn,
      renewalStatus,
      renewalTokens,
    };
    this.formSaving.set(true);
    this.api
      .updateUserTokenQuota(quotaId, body)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.formSaving.set(false);
          this.toast.success('Token allocation updated.');
          this.closeForm();
          this.reloadDrawerData();
        },
        error: (err: unknown) => {
          this.formSaving.set(false);
          this.toast.error(this.extractErrorMessage(err, 'Could not update allocation.'));
        },
      });
  }

  confirmDelete(row: UserTokenQuota): void {
    this.pendingDeleteRow = row;
    this.deleteConfirmUserLabel = this.userRowLabel(row);
    this.confirmationService.confirm({
      key: this.deleteQuotaConfirmKey,
      header: 'Remove token allocation',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Remove',
      rejectLabel: 'Cancel',
      rejectButtonStyleClass: 'p-button-secondary',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.executeDelete(),
    });
  }

  private executeDelete(): void {
    const row = this.pendingDeleteRow;
    this.pendingDeleteRow = null;
    if (!row) {
      return;
    }
    this.api
      .deleteUserTokenQuota(row.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toast.success('Token allocation removed.');
          this.reloadDrawerData();
        },
        error: (err: unknown) => {
          this.toast.error(this.extractErrorMessage(err, 'Could not remove allocation.'));
        },
      });
  }

  restoreQuota(row: UserTokenQuota): void {
    const quotaId = row.id?.trim();
    if (!quotaId || this.restoringQuotaId()) {
      return;
    }
    this.restoringQuotaId.set(quotaId);
    this.api
      .restoreUserTokenQuota(quotaId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.restoringQuotaId.set(null);
          this.toast.success('Token allocation restored.');
          this.reloadDrawerData();
        },
        error: (err: unknown) => {
          this.restoringQuotaId.set(null);
          this.toast.error(this.extractErrorMessage(err, 'Could not restore allocation.'));
        },
      });
  }

  userRowLabel(row: UserTokenQuota): string {
    const name = row.userName?.trim();
    if (name) {
      return name;
    }
    return row.userEmail?.trim() || 'User';
  }

  private loadDrawerData(): void {
    this.drawerLoading.set(true);
    this.accessDenied.set(false);
    this.listError.set(null);
    this.drawerDataLoaded.set(false);
    this.showActivities.set(false);
    this.showUserMetrics.set(false);
    this.skipNextLazyLoad = true;
    forkJoin({
      summary: this.api.getCompanyTokenAllocationSummary(),
      quotas: this.api.listUserTokenQuotas({
        page: 1,
        limit: this.pageSize(),
        sortBy: 'userName',
        sortDir: 'asc',
        status: this.quotaListView(),
        q: this.searchText().trim() || undefined,
      }),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ summary, quotas }) => {
          this.companySummary.set(summary);
          this.quotas.set(quotas.items);
          this.totalRecords.set(quotas.total);
          this.page.set(quotas.page);
          this.tableFirst.set(0);
          this.drawerDataLoaded.set(true);
          this.drawerLoading.set(false);
          this.cdr.markForCheck();
        },
        error: (err: unknown) => {
          this.drawerLoading.set(false);
          this.drawerDataLoaded.set(false);
          if (this.isForbidden(err)) {
            this.accessDenied.set(true);
          } else {
            this.listError.set(this.extractErrorMessage(err, 'Could not load token allocation.'));
          }
          this.cdr.markForCheck();
        },
      });
  }

  private reloadDrawerData(): void {
    if (!this.visible()) {
      return;
    }
    this.loadDrawerData();
  }

  private loadQuotas(): void {
    if (this.accessDenied() || this.drawerLoading()) {
      return;
    }
    this.loading.set(true);
    this.listError.set(null);
    this.api
      .listUserTokenQuotas({
        page: this.page(),
        limit: this.pageSize(),
        status: this.quotaListView(),
        q: this.searchText().trim() || undefined,
        sortBy: 'userName',
        sortDir: 'asc',
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.quotas.set(res.items);
          this.totalRecords.set(res.total);
          this.loading.set(false);
          this.cdr.markForCheck();
        },
        error: (err: unknown) => {
          this.loading.set(false);
          if (this.isForbidden(err)) {
            this.accessDenied.set(true);
          } else {
            this.listError.set(this.extractErrorMessage(err, 'Could not load user quotas.'));
          }
          this.cdr.markForCheck();
        },
      });
  }

  private loadActivities(): void {
    if (this.accessDenied()) {
      return;
    }
    this.activitiesLoading.set(true);
    this.activitiesError.set(null);
    this.api
      .listTokenAllocationActivities({
        page: this.activitiesPage(),
        limit: this.activitiesPageSize(),
        q: this.activitySearchText().trim() || undefined,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.activities.set(res.items);
          this.activitiesTotal.set(res.total);
          this.activitiesLoading.set(false);
          this.cdr.markForCheck();
        },
        error: (err: unknown) => {
          this.activitiesLoading.set(false);
          this.activitiesError.set(this.extractErrorMessage(err, 'Could not load activities.'));
          this.cdr.markForCheck();
        },
      });
  }

  private loadAssignableUsers(): void {
    this.assignableUsersLoading.set(true);
    this.api
      .listAssignableUsers()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (items) => {
          this.assignableUserOptions.set(
            items.map((u) => ({
              ...u,
              label: assignableUserLabel(u),
            })),
          );
          this.assignableUsersLoading.set(false);
          this.cdr.markForCheck();
        },
        error: (err: unknown) => {
          this.assignableUsersLoading.set(false);
          this.toast.error(this.extractErrorMessage(err, 'Could not load users.'));
          this.cdr.markForCheck();
        },
      });
  }

  private resetDrawerState(): void {
    this.searchText.set('');
    this.searchInput.set('');
    this.activitySearchText.set('');
    this.activitySearchInput.set('');
    this.page.set(1);
    this.tableFirst.set(0);
    this.activitiesPage.set(1);
    this.activitiesTableFirst.set(0);
    this.showActivities.set(false);
    this.showUserMetrics.set(false);
    this.metricsQuota.set(null);
    this.usageMetrics.set(null);
    this.usageMetricsError.set(null);
    this.usageMetricsLoading.set(false);
    this.usageMetricsPeriod.set('this_month');
    this.accessDenied.set(false);
    this.listError.set(null);
    this.activitiesError.set(null);
    this.companySummary.set(null);
    this.quotas.set([]);
    this.quotaListView.set('active');
    this.restoringQuotaId.set(null);
    this.activities.set([]);
    this.totalRecords.set(0);
    this.activitiesTotal.set(0);
    this.drawerDataLoaded.set(false);
    this.skipNextLazyLoad = false;
    this.skipNextActivityLazyLoad = false;
    this.drawerLoading.set(false);
    this.addTokensVisible.set(false);
    this.addTokensSaving.set(false);
    this.addTokensState = null;
  }

  private emptyForm(mode: QuotaFormMode): QuotaFormState {
    return {
      mode,
      selectedUserId: null,
      allocatedTokens: 0,
      usedTokens: 0,
      renewalType: 0,
      renewalOn: null,
      renewalStatus: 1,
      renewalTokens: 0,
    };
  }

  private isForbidden(err: unknown): boolean {
    const status = (err as { status?: number })?.status;
    return status === 403;
  }

  private extractErrorMessage(err: unknown, fallback: string): string {
    const e = err as { error?: { message?: string | string[] }; message?: string };
    const msg = e?.error?.message;
    if (Array.isArray(msg) && msg.length) {
      return String(msg[0]);
    }
    if (typeof msg === 'string' && msg.trim()) {
      return msg.trim();
    }
    if (typeof e?.message === 'string' && e.message.trim()) {
      return e.message.trim();
    }
    return fallback;
  }
}
